import { WEB_PAGES } from '@praxadon/shared/pages';
import { Button, Field, Input } from '@praxadon/ui';
import { type SubmitEvent, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { ApiError, describeError } from '../../api/client.ts';
import { auth } from '../../api/endpoints.ts';
import { useSession } from '../../app/session.tsx';
import { AuthLayout, FormError } from './AuthLayout.tsx';
import { PasswordInput, passwordHint } from './PasswordInput.tsx';
import { Turnstile, type TurnstileHandle } from './Turnstile.tsx';

const backToSignIn = (
  <Link className="underline hover:text-fg" to={WEB_PAGES.login}>
    Back to sign in
  </Link>
);

/**
 * Asking for a reset link. The answer is the same whether or not the account exists —
 * anything else would let a stranger find out who is in the class.
 */
export function ForgotPage() {
  const { site } = useSession();
  const [identifier, setIdentifier] = useState('');
  const [token, setToken] = useState<string | undefined>();
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const turnstile = useRef<TurnstileHandle>(null);

  if (sent) {
    return (
      <AuthLayout
        form={false}
        file="reset.html"
        title="Check your email"
        subtitle="If that account has an email address, a reset link is on its way. It works for 30 minutes."
        footer={backToSignIn}
      >
        <p className="text-center text-sm text-fg-muted">
          No email address on your account? Your instructor can reset your password for you.
        </p>
      </AuthLayout>
    );
  }

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await auth.forgot({ identifier, turnstileToken: token });
      setSent(true);
    } catch (failure) {
      setError(describeError(failure));
      turnstile.current?.reset();
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      file="reset.html"
      title="Reset your password"
      subtitle="We'll email you a link to choose a new one."
      footer={backToSignIn}
    >
      <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-4">
        <FormError message={error} />
        <Field label="Username or email">
          <Input
            autoComplete="username"
            autoCapitalize="none"
            value={identifier}
            onChange={(event) => {
              setIdentifier(event.target.value);
            }}
          />
        </Field>
        {site.turnstileSiteKey ? (
          <Turnstile ref={turnstile} siteKey={site.turnstileSiteKey} onToken={setToken} />
        ) : null}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={busy}
          disabled={!identifier || (site.turnstileSiteKey !== null && !token)}
        >
          Send reset link
        </Button>
      </form>
    </AuthLayout>
  );
}

/** Choosing a new password from the link in the email. */
export function ResetPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  if (done) {
    return (
      <AuthLayout form={false} file="reset.html" title="Password changed" subtitle={done}>
        <Link
          to={WEB_PAGES.login}
          className="flex h-11 items-center justify-center rounded-lg bg-accent font-medium text-accent-fg"
        >
          Sign in
        </Link>
      </AuthLayout>
    );
  }

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFieldError(undefined);
    try {
      const result = await auth.reset({ token, password });
      setDone(result.message);
    } catch (failure) {
      if (failure instanceof ApiError && failure.fields['password'])
        setFieldError(failure.fields['password']);
      else setError(describeError(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout file="reset.html" title="Choose a new password" footer={backToSignIn}>
      <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-4">
        <FormError
          message={
            token ? error : 'This reset link is incomplete. Please use the link from your email.'
          }
        />
        <Field label="New password" hint={passwordHint(password)} error={fieldError}>
          <PasswordInput
            autoComplete="new-password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
        </Field>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={busy}
          disabled={!token || !password}
        >
          Change password
        </Button>
      </form>
    </AuthLayout>
  );
}
